import { delay } from 'redux-saga'
import normalize from 'jsonapi-normalizer'
import T from 'i18n-react'
import {
  takeLatest,
  take,
  fork,
  all,
  call,
  select,
  put,
} from 'redux-saga/effects'

import {
  buildUrl,
} from 'helpers/actionHelper'

import {
  getRequest,
  patch,
} from 'helpers/RequestUtil'

import {
  countSelector,
  pageSizeSelector,
  currentPageSelector,
  searchValueSelector,
  editSelector,
} from './selectors'

import {
  CHANGE_EDIT,
  CHANGE_CURRENT_PAGE,
  CHANGE_PAGE_SIZE,
  CHANGE_SEARCH_VALUE,
  CHANGE_SELECT_CLIENT,
  FETCH_CLIENTS,
  PATCH_CLIENT
} from './constants'

import {
  setActiveDialog,
  setActiveSiteDialog,
} from 'containers/Admin/Client/Client/actions'

import {
  success,
  error,
} from 'containers/App/Notification/actions'

import {
  updateEdit,
  updatePageSize,
  updateCountSize,
  updateCurrentPage,
  updateSearchValue,
  updateSelectClient,
  clearClientList,
  updateClientList,
  fetchClientsStart,
  fetchClientsSuccess,
  fetchClientsError,
  startFetchClientsAsync,
  patchClientStart,
  patchClientSuccess,
  patchClientError,
  startPatchClientAsync,
} from './actions'

import {
  updateTablesAction,
} from 'containers/Entity/actions'

function* patchClient({id, enable, ...value}) {
  try {
    yield put(startPatchClientAsync())
    yield put(patchClientStart())
    const data = yield call(
      patch,
      id,
      'clients',
      { enable: !enable },
      '/api/fulcrum/clients'
    )
    const normalized = {
      result: { clients: [id] },
      entities: { clients: { [id]: { id, enable: !enable, ...value } } }
    }
    yield put(updateTablesAction(normalized))
    yield put(patchClientSuccess())
    yield put(success(T.translate(
      'pages.admin.clients.notification.post_enable_success',
      {
        name: value.name,
        status: !enable ? 'Enabled' : 'Disabled'
      }
    )))
    return data
  } catch(error) {
    yield put(patchClientError(error))
    yield put(error(T.translate('pages.admin.clients.notification.post_enable_error', {name: value.name})))
  }
}

function* fetchClients(url) {
  try {
    yield put(startFetchClientsAsync())
    yield put(fetchClientsStart())
    const data = yield call(
      getRequest,
      url
    )
    const normalized = normalize(data)
    yield put(updateTablesAction(normalized))
    yield put(fetchClientsSuccess())
    return data
  } catch(error) {
    yield put(fetchClientsError(error))
  }
}

function* handlePatchClient(action) {
  const { value } = action
  yield call(patchClient, value)
}

function* handleFetchClients() {
  let params = []
  yield call(delay, 500)
  const edit = yield select(editSelector)
  const page = yield select(currentPageSelector)
  const size = yield select(pageSizeSelector)
  const search = yield select(searchValueSelector)

  if (!edit)
    params = [...params, { type: 'enable', value: true }]
  if (search)
    params = [...params, { type: 'search', value: search }]

  const url = buildUrl('/api/fulcrum/clients', ...params)
    .concat(`&page[number]=${page}&page[size]=${size}`)

  const data = yield call(fetchClients, url)
  const normalized = normalize(data)
  const { result: { clients } } = normalized
  yield put(updateCountSize(data.meta.totalCount))
  yield put(updateClientList(clients))
}

function* handleChangePageSize(action) {
  const { size } = action
  const page = yield select(currentPageSelector)
  const prevSize = yield select(pageSizeSelector)
  let newPage = Math.floor((page - 1) * prevSize / size)
  newPage = (newPage > 0)
    ? newPage
    : newPage + 1

  yield put(updatePageSize(size))
  yield put(updateCurrentPage(newPage))
  yield fork(handleFetchClients)
}

function* handleChangeCurrentPage(action) {
  const { value } = action
  const page = yield select(currentPageSelector)
  yield put(updateCurrentPage(page + value))
  yield fork(handleFetchClients)
}

function* handleSearchChange(action) {
  const { value } = action
  yield put(updateCurrentPage(1))
  yield put(clearClientList())
  yield put(updateSearchValue(value))
  yield fork(handleFetchClients)
}

function* handleChangeSelectClient(action) {
  const { id } = action
  yield put(updateSelectClient(id))
  yield put(setActiveSiteDialog())
}

function* handleChangeEdit(action) {
  const { enable } = action
  yield put(updateCurrentPage(1))
  yield put(updateEdit(enable))
  yield put(clearClientList())
  yield fork(handleFetchClients)
}

function* watchChangeSelectClient() {
  yield takeLatest(CHANGE_SELECT_CLIENT, handleChangeSelectClient)
}

function* watchPatchClient() {
  yield takeLatest(PATCH_CLIENT, handlePatchClient)
}

function* watchFetchClients() {
  yield takeLatest(FETCH_CLIENTS, handleFetchClients)
}

function* watchChangePageSize() {
  yield takeLatest(CHANGE_PAGE_SIZE, handleChangePageSize)
}

function* watchChangeCurrentPage() {
  yield takeLatest(CHANGE_CURRENT_PAGE, handleChangeCurrentPage)
}

function* watchSearchChange() {
  yield takeLatest(CHANGE_SEARCH_VALUE, handleSearchChange)
}

function* watchChangeEdit() {
  yield takeLatest(CHANGE_EDIT, handleChangeEdit)
}

function* loadSagas() {
  yield all([
    fork(watchFetchClients),
    fork(watchChangePageSize),
    fork(watchChangeCurrentPage),
    fork(watchSearchChange),
    fork(watchPatchClient),
    fork(watchChangeSelectClient),
    fork(watchChangeEdit)
  ])
}

export default loadSagas
